import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
} from "@angular/core";

import { forkJoin, of } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MaterialModule } from "src/app/app.materials";
import { FormsModule } from "@angular/forms";
import { HouseViewsService } from "../../shared/services/house-views.service";
import { PortfolioService } from "../../pages/portfolio/portfolio.service";
import { NotificationService } from "../../shared/services/notification.service";
import { ViewsService } from "../../shared/services/views.service";
import { HouseViewsReadonlyComponent } from "../house-views-readonly/house-views-readonly.component";
import { ViewsSetterComponent } from "../views-setter/views-setter.component";
import { EffectiveViewsComponent } from "../effective-views/effective-views.component";
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from "../confirm-dialog/confirm-dialog.component";

@Component({
  selector: "app-optimization-views-panel",
  templateUrl: "./optimization-views-panel.component.html",
  styleUrls: ["./optimization-views-panel.component.scss"],
  standalone: true,
  imports: [
    MaterialModule,
    FormsModule,
    HouseViewsReadonlyComponent,
    ViewsSetterComponent,
    EffectiveViewsComponent,
  ],
})
export class OptimizationViewsPanelComponent implements OnInit {
  @Input() portfolio: any;
  @Input() optimizationData: any;
  @Input() constraintLists: any;
  // Forwarded to effective-views so its Optimize button disables + spins while
  // a run is computing.
  @Input() optimizing: boolean = false;

  @Output() optimize = new EventEmitter<void>();
  @Output() frequencyChanged = new EventEmitter<string>();

  firmViews: any[] = [];
  userViews: any[] = [];

  firmEnabled: Record<string, boolean> = {};
  userEnabled: Record<string, boolean> = {};

  effectivePayload: any[] = [];
  payloadLoading = false;
  payloadError = "";

  resetLoading = false;
  resetCompleted = false;

  frequencyConfirmedThisSession = false;

  constructor(
    private houseViewsService: HouseViewsService,
    private portfolioService: PortfolioService,
    private notificationService: NotificationService,
    private viewsService: ViewsService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    forkJoin({
      firm: this.houseViewsService.getFirmViews(),
      user: this.houseViewsService.getUserViews(),
    }).subscribe((resp: any) => {
      this.firmViews = resp.firm || [];
      this.userViews = resp.user || [];

      this.firmViews.forEach((v) => {
        this.firmEnabled[this.getViewKey(v)] = true;
      });
      this.userViews.forEach((v) => {
        this.userEnabled[this.getViewKey(v)] = true;
      });

    });

    this.loadEffectiveViews();
  }

  private loadEffectiveViews(): void {
    if (!this.portfolio?.id) return;

    this.payloadLoading = true;
    this.payloadError = "";
    this.effectivePayload = [];

    this.portfolioService.fetchEffectiveViews(this.portfolio.id).subscribe(
      (resp: any) => {
        this.payloadLoading = false;

        if (resp.error) {
          this.payloadError = resp.error;
          return;
        }

        const views = Array.isArray(resp) ? resp : resp.success || resp;
        this.effectivePayload = (Array.isArray(views) ? views : []).map(
          (item) => {
            const firstItem = item.items?.[0];
            const itemId = firstItem?.id ?? item.item_id;
            const typeStr = item.type?.name || item.type?.id || item.type;
            const resolvedName =
              firstItem?.name_short ||
              firstItem?.index_name_short ||
              firstItem?.name ||
              (itemId != null ? this.resolveItemName(+itemId, typeStr) : "") ||
              item.type?.name ||
              item.type;
            return {
              ...item,
              type: typeStr,
              resolvedName,
            };
          },
        );
      },
      (err: any) => {
        this.payloadLoading = false;
        this.payloadError =
          err?.error?.error || "Failed to load effective views.";
      },
    );
  }

  getViewKey(v: any): string {
    const typeId = v.type?.id || v.type;
    const itemId = v.items?.[0]?.id || "unknown";
    // Relative views: {type}_{itemId}_vs_{sorted_items2_ids_joined_by_hyphen}
    if (v.items2 && v.items2.length) {
      const sortedItems2 = v.items2
        .map((i: any) => +i.id)
        .sort((a, b) => a - b)
        .join("-");
      return `${typeId}_${itemId}_vs_${sortedItems2}`;
    }
    return `${typeId}_${itemId}`;
  }

  onFirmMasterToggle(enabled: boolean): void {
    this.firmEnabled = {};
    this.firmViews.forEach((v) => {
      this.firmEnabled[this.getViewKey(v)] = enabled;
    });
    this.firmEnabled = { ...this.firmEnabled };
  }

  onUserMasterToggle(enabled: boolean): void {
    this.userEnabled = {};
    this.userViews.forEach((v) => {
      this.userEnabled[this.getViewKey(v)] = enabled;
    });
    this.userEnabled = { ...this.userEnabled };
  }

  onFirmViewToggle(event: { viewKey: string; enabled: boolean }): void {
    this.firmEnabled = { ...this.firmEnabled, [event.viewKey]: event.enabled };
  }

  onUserViewToggle(event: { viewKey: string; enabled: boolean }): void {
    this.userEnabled = { ...this.userEnabled, [event.viewKey]: event.enabled };
  }

  resolveItemName(itemId: any, type: string): string {
    if (itemId == null) return "";
    const idStr = String(itemId);

    // Search firm/user views and portfolio views first - they have name_short
    const allViews = [
      ...this.firmViews,
      ...this.userViews,
      ...(this.optimizationData?.views || []),
    ];
    for (const v of allViews) {
      const vType = v.type?.id || v.type;
      if (vType === type && v.items) {
        const found = v.items.find((i: any) => String(i.id) === idStr);
        if (found)
          return found.name_short || found.name || found.name_full || idStr;
      }
    }

    // Fall back to constraintLists by type
    const listMap: Record<string, string> = {
      asset: "assets",
      sector: "sectors",
      country: "countries",
      index: "benchmarks",
      currency: "benchmarks",
    };
    const listKey = listMap[type];
    if (listKey && this.constraintLists?.[listKey]) {
      const list = this.constraintLists[listKey];
      // Benchmarks have a nested structure (groups with items)
      if (type === "index" || type === "currency") {
        for (const group of list) {
          if (group.items) {
            const found = group.items.find((i: any) => String(i.id) === idStr);
            if (found)
              return (
                found.name_short ||
                found.index_name_short ||
                found.name ||
                found.name_full ||
                found.index_name_full ||
                idStr
              );
          }
          if (String(group.id) === idStr)
            return (
              group.name_short ||
              group.index_name_short ||
              group.name ||
              group.name_full ||
              idStr
            );
        }
      } else {
        const found = list.find((i: any) => String(i.id) === idStr);
        if (found)
          return found.name_short || found.name || found.company_name || idStr;
      }
    }

    return idStr;
  }

  generatePayload(): void {
    this.payloadLoading = true;
    this.payloadError = "";
    this.effectivePayload = [];

    const toggles: Record<string, string> = {};

    // Only include toggles for views that have been explicitly toggled off or
    // where the user wants a specific level to win over the default priority
    this.firmViews.forEach((v) => {
      const key = this.getViewKey(v);
      if (this.firmEnabled[key] !== false) {
        toggles[key] = "firm";
      }
    });

    // User views override firm for same key
    this.userViews.forEach((v) => {
      const key = this.getViewKey(v);
      if (this.userEnabled[key] !== false) {
        toggles[key] = "user";
      }
    });

    // Always include the toggles map so the backend persists the exact state,
    // including the "all disabled" case (e.g. after a reset).
    const body = { toggles };

    this.portfolioService
      .generateViewsPayload(this.portfolio.id, body as any)
      .subscribe(
        (resp: any) => {
          this.payloadLoading = false;

          if (resp.error) {
            this.payloadError = resp.error;
            return;
          }

          const data = resp.success || resp;
          this.effectivePayload = (data.payload || []).map((item) => ({
            ...item,
            resolvedName:
              item.name_short ||
              item.index_name_short ||
              this.resolveItemName(item.item_id, item.type),
          }));

          if (data.frequency_changed && !this.frequencyConfirmedThisSession) {
            this.frequencyChanged.emit(data.enforced_frequency);
            const dialogData = new ConfirmDialogModel(
              "Frequency Change",
              `The model frequency has been changed from ${data.previous_frequency} to ${data.enforced_frequency} because a lower-frequency view was activated.`,
              true,
            );
            this.dialog
              .open(ConfirmDialogComponent, {
                maxWidth: "400px",
                data: dialogData,
              })
              .afterClosed()
              .subscribe(() => {
                this.frequencyConfirmedThisSession = true;
              });
          }

        },
        (err: any) => {
          this.payloadLoading = false;
          this.payloadError =
            err?.error?.error || "An unexpected error occurred.";
        },
      );
  }

  onResetViews(): void {
    const dialogData = new ConfirmDialogModel(
      "Reset Views",
      "Do you want to remove all the portfolio views and disable the house views?",
      false,
    );

    this.dialog
      .open(ConfirmDialogComponent, {
        maxWidth: "420px",
        data: dialogData,
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.performReset();
      });
  }

  private performReset(): void {
    this.resetLoading = true;
    this.payloadError = "";

    // Disable all firm and user house views
    this.onFirmMasterToggle(false);
    this.onUserMasterToggle(false);

    // Delete all portfolio views (if any) in parallel
    const portfolioViews: any[] = this.optimizationData?.views || [];
    const deletions = portfolioViews.length
      ? forkJoin(portfolioViews.map((v) => this.viewsService.delete(v)))
      : of([]);

    deletions.subscribe(
      () => {
        if (this.optimizationData) {
          this.optimizationData.views = [];
        }
        this.resetCompleted = true;
        this.persistResetPayload();
      },
      (err: any) => {
        this.resetLoading = false;
        this.payloadError = err?.error?.error || "Failed to reset views.";
      },
    );
  }

  // Posts a fresh generate-payload request so the backend persists the
  // post-reset state (all house views disabled, portfolio views cleared).
  private persistResetPayload(): void {
    this.portfolioService
      .generateViewsPayload(this.portfolio.id, { toggles: {} })
      .subscribe(
        (resp: any) => {
          this.resetLoading = false;

          if (resp?.error) {
            this.payloadError = resp.error;
            this.effectivePayload = [];
            return;
          }

          const data = resp?.success || resp || {};
          this.effectivePayload = (data.payload || []).map((item: any) => ({
            ...item,
            resolvedName:
              item.name_short ||
              item.index_name_short ||
              this.resolveItemName(item.item_id, item.type),
          }));
        },
        (err: any) => {
          this.resetLoading = false;
          this.effectivePayload = [];
          this.payloadError =
            err?.error?.error || "Failed to persist reset state.";
        },
      );
  }
}
